import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { parsePhoneNumber } from '@/helper/phoneUtils';
import { showToast } from '@/helper/toast.helper';
import { AppDispatch, RootState } from '@/store';
import { getAllRoles } from '@/store/slices/adminUsersSlice/adminUserSlice';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import { Language } from '@/types/metaData';

import { createProjects, updateProjects } from '@/store/slices/projectsSlice/projectsSlice';
import {
  AdminUser,
  CreateProjectResponseDto,
  FromSource,
  GeoFenceInputDto,
  Project,
  SiteFormData,
} from '@/types/project.types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import BasicInformation from './BasicInformation';
import ProjectCreatedPopup from './ProjectCreatedPopup';
import ProjectSiteLocations from './ProjectSiteLocations';
import ReviewAndCreate from './ReviewAndCreate';
import RoleAssignment from './RoleAssignment';

interface LocationState {
  from?: FromSource;
  project?: {
    id: string;
    name: string;
    maxUsers: string;
    maxSites: string;
    sites: SiteFormData[];
    primaryColor: string;
    secondaryColor: string;
    emoji: string;
    description: string;
    admins: AdminUser[];
  };
}

const CreateProject: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const state = location.state as LocationState;
  const isEditMode = Boolean(state?.project);

  const defaultSites: SiteFormData[] = [
    {
      name: '',
      countryId: 0,
      languages: [],
      currency: '',
      maxUsers: undefined,
      geofence: {
        centerLat: null,
        centerLng: null,
        name: '',
        radiusMeters: null,
        timezone: '',
      },
    },
  ];

  const [projectName, setProjectName] = useState<string>(state?.project?.name || '');
  const [sites, setSites] = useState<SiteFormData[]>(
    state?.project?.sites?.length
      ? state.project.sites.map((s) => ({
          id: s.id,
          name: s.name,
          countryId: s.countryId,
          currency: s.currency,
          maxUsers: s.maxUsers,
          geofence: {
            centerLat: s.geofence.centerLat,
            centerLng: s.geofence.centerLng,
            radiusMeters: s.geofence.radiusMeters,
            name: s.geofence.name,
            timezone: s.geofence.timezone,
          },
          geofenceId: s.geofenceId,
          languages: Array.isArray(s.languages)
            ? typeof s.languages[0] === 'number'
              ? s.languages
              : s.languages.map((l: any) => l.id)
            : [],
        }))
      : defaultSites,
  );

  const [error, setError] = useState<string>('');
  const [admins, setAdmins] = useState<AdminUser[]>(
    state?.project?.admins?.length
      ? state.project.admins.map((admin) => ({
          roleId: admin.roleId,
          phoneNumber: admin.countryCode + admin.phoneNumber,
          message: admin.message,
          blueNumberId: admin.blueNumberId,
        }))
      : [{ roleId: '', phoneNumber: '', message: '', blueNumberId: '' }],
  );

  const [primaryColour, setPrimaryColour] = useState(state?.project?.primaryColor || '');
  const [secondaryColour, setSecondaryColour] = useState(state?.project?.secondaryColor || '');
  const [selectedEmoji, setSelectedEmoji] = useState(state?.project?.emoji || '');
  const [description, setDescription] = useState(state?.project?.description || '');
  const [step, setStep] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [project, setProject] = useState<Project | null>();

  const [isStep0Valid, setIsStep0Valid] = useState(false);
  const [isStep1Valid, setIsStep1Valid] = useState(false);
  const [isStep2Valid, setIsStep2Valid] = useState(true);

  const roleAssignmentRef = useRef<{
    validatePendingFields: () => Promise<void>;
    hasErrors: () => boolean;
    clearAllErrors: () => void;
    isAllValidated: () => boolean;
  }>(null);

  const originalProjectRef = useRef<LocationState['project'] | null>(null);
  // const canCreate = usePermission(, 'project', 'create')

  useEffect(() => {
    if (isEditMode && state?.project && !originalProjectRef.current) {
      originalProjectRef.current = state.project;
    }
  }, [isEditMode, state?.project]);

  const navigate = useNavigate();

  const { countries } = useSelector((state: RootState) => state.metaData);

  const isSiteValid = (site: SiteFormData) => {
    if (!site.name.trim() || site.name.trim().length < 5 || site.name.trim().length > 60)
      return false;
    if (!site.countryId) return false;
    if (!site.currency) return false;
    if (site.languages.length === 0) return false;
    if (site.geofence.centerLat === null || site.geofence.centerLng === null) return false;
    if (site.geofence.radiusMeters === null || site.geofence.radiusMeters <= 0) return false;

    return true;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      validateSiteScilently();
    }, 500);

    return () => clearTimeout(handler);
  }, [sites]);

  const validateSiteScilently = () => {
    const allValid = sites.every(isSiteValid);
    setIsStep2Valid(allValid);
    return allValid;
  };

  const validateSite = (): boolean => {
    let allValid = true;

    sites.forEach((site, index) => {
      if (!site.name.trim()) {
        showToast.error(`Site name is required for Site ${index + 1}.`);
        allValid = false;
      } else if (site.name.trim().length < 5 || site.name.trim().length > 60) {
        showToast.error(`Site name must be 5–60 characters for Site ${index + 1}.`);
        allValid = false;
      } else if (!site.countryId) {
        showToast.error(`Please select a country for Site ${index + 1}.`);
        allValid = false;
      } else if (!site.currency) {
        showToast.error(`Please select at least 1 currency for Site ${index + 1}.`);
        allValid = false;
      } else if (site.languages.length === 0) {
        showToast.error(`Please select at least 1 language for Site ${index + 1}.`);
        allValid = false;
      } else if (site.geofence.centerLat === null || site.geofence.centerLng === null) {
        showToast.error(`Please complete the geofence details for Site ${index + 1}.`);
        allValid = false;
      } else if (site.geofence.radiusMeters === null || site.geofence.radiusMeters <= 0) {
        showToast.error(`Please enter a valid radius (greater than 0) for Site ${index + 1}.`);
        allValid = false;
      }
    });

    setIsStep2Valid(allValid);

    return allValid;
  };

  const [resetKey, setResetKey] = useState(0);

  const handleSiteChange = (
    index: number,
    field: keyof SiteFormData,
    value: string | number | number[] | GeoFenceInputDto | Language[],
  ) => {
    const updatedSites = [...sites];
    updatedSites[index] = { ...updatedSites[index], [field]: value };
    setSites(updatedSites);
  };

  const addSite = () => {
    // Validate all existing sites before adding a new one
    const allSitesValid = sites.every((site, index) => {
      return (
        site.name.trim() !== '' &&
        site.name.trim().length >= 5 &&
        site.name.trim().length <= 60 &&
        site.countryId &&
        site.geofence.centerLat !== null &&
        site.geofence.centerLng !== null &&
        site.geofence.radiusMeters !== null &&
        site.geofence.radiusMeters > 0 &&
        site.languages.length > 0 &&
        site.currency.trim() !== ''
      );
    });

    if (!allSitesValid) {
      showToast.error(
        'Please complete all required fields in existing sites before adding a new site.',
      );
      return;
    }

    setSites([
      ...sites,
      {
        name: '',
        countryId: 0,
        languages: [],
        currency: '',
        maxUsers: undefined,
        geofence: {
          centerLat: null,
          centerLng: null,
          name: '',
          radiusMeters: null,
          timezone: '',
        },
      },
    ]);
  };

  const handleReset = () => {
    setResetKey((k) => k + 1);

    if (isEditMode && originalProjectRef.current) {
      const project = originalProjectRef.current;

      switch (step) {
        case 0:
          setProjectName(project.name);
          setPrimaryColour(project.primaryColor ?? '');
          setSecondaryColour(project.secondaryColor ?? '');
          setSelectedEmoji(project.emoji);
          setDescription(project.description ?? '');
          break;

        case 1:
          setAdmins(
            project.admins.map((admin) => ({
              roleId: admin.roleId,
              phoneNumber: admin.countryCode + admin.phoneNumber,
              message: admin.message,
              blueNumberId: admin.blueNumberId,
            })),
          );
          break;

        case 2:
          setSites(
            project.sites.map((s) => ({
              id: s.id,
              name: s.name,
              countryId: s.countryId,
              currency: s.currency,
              maxUsers: s.maxUsers === 0 ? undefined : s.maxUsers,
              geofence: {
                centerLat: s.geofence.centerLat === 0 ? null : s.geofence.centerLat,
                centerLng: s.geofence.centerLng === 0 ? null : s.geofence.centerLng,
                radiusMeters: s.geofence.radiusMeters,
                name: s.geofence.name,
                timezone: s.geofence.timezone,
              },
              geofenceId: s.geofenceId,
              languages: Array.isArray(s.languages)
                ? typeof s.languages[0] === 'number'
                  ? s.languages
                  : s.languages.map((l: any) => l.id)
                : [],
            })),
          );
          break;
      }
      return;
    }

    // CREATE MODE
    switch (step) {
      case 0:
        setProjectName('');
        setDescription('');
        setSelectedEmoji('');
        setPrimaryColour('');
        setSecondaryColour('');
        break;
      case 1:
        setAdmins([{ roleId: '', phoneNumber: '', message: '', blueNumberId: '' }]);
        break;
      case 2:
        setSites(defaultSites);
        break;
    }
  };

  const handleCreate = async () => {
    if (!isConfirmed) {
      setError('Please confirm before ' + (isEditMode ? 'updating' : 'creating') + ' the project.');
      return;
    }
    setError('');
    setIsCreating(true);

    try {
      const projectData = {
        name: projectName,
        descriptions: description,
        primaryColour,
        secondaryColour,
        emoji: selectedEmoji,
        managers: admins
          .filter((admin) => admin.phoneNumber && admin.blueNumberId)
          .map((admin) => {
            const parsedPhone = (() => {
              try {
                return parsePhoneNumber(admin.phoneNumber);
              } catch {
                return null;
              }
            })();

            return {
              roleId: Number(admin.roleId) || 1,
              countryCode: parsedPhone?.countryCode || '',
              phoneNumber: parsedPhone?.phoneNumber || '',
              message: admin.message,
              blueNumberId: admin.blueNumberId,
            };
          }),

        sites: sites
          .filter((site) => site.name && site.countryId)
          .map((site) => {
            const country = countries.find((c) => c.id === site.countryId);

            return {
              id: site.id,
              name: site.name,
              countryId: site.countryId,
              currency: site.currency,
              maxUsers: site.maxUsers || 0,
              languages: site.languages || [],
              geofence: {
                id: site.geofenceId,
                name: `${country?.name || 'Unknown'} ${site.name} Geofence`,
                centerLat: site.geofence.centerLat || null,
                centerLng: site.geofence.centerLng || null,
                radiusMeters: site.geofence.radiusMeters || null,
                timezone: site.geofence.timezone || '',
              },
              geofenceId: site.geofenceId,
            };
          }),
      };
      let result;
      if (isEditMode && state?.project?.id) {
        result = await dispatch(
          updateProjects({
            id: state?.project?.id,
            data: projectData,
          }),
        ).unwrap();
      } else {
        result = await dispatch(createProjects(projectData)).unwrap();
      }
      setProject((result as CreateProjectResponseDto).data);
      setShowPopup(true);
    } catch (error: unknown) {
      setError(error as string);
      showToast.error(error as string);
    } finally {
      setIsCreating(false);
    }
  };

  const handleNext = async () => {
    setError('');
    if (step === 1 && roleAssignmentRef.current) {
      await roleAssignmentRef.current.validatePendingFields();
    }
    let isValid = false;
    switch (step) {
      case 0:
        isValid = isStep0Valid;
        break;
      case 1:
        isValid = isStep1Valid;
        break;
      case 2:
        isValid = validateSite();
        break;
      case 3:
        isValid = true;
        break;
      default:
        isValid = false;
    }

    if (isValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBackStep = () => {
    setError('');
    if (step === 1) {
      setIsStep1Valid(false);
      // Also clear validation states in the ref
      if (roleAssignmentRef.current) {
        roleAssignmentRef.current.clearAllErrors();
      }
    }
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleAdminChange = (index: number, field: keyof AdminUser, value: string | number) => {
    // Check for duplicates when phone number or blue number is changed
    if ((field === 'phoneNumber' || field === 'blueNumberId') && value) {
      const isDuplicate = admins.some((admin, i) => {
        if (i === index) return false; // Skip current admin
        return field === 'phoneNumber' ? admin.phoneNumber === value : admin.blueNumberId === value;
      });

      if (isDuplicate) {
        const fieldName = field === 'phoneNumber' ? 'phone number' : 'Blue Number ID';
        showToast.error(`This ${fieldName} is already added in this Project.`);
        return;
      }
    }

    setAdmins((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addAdmin = () => {
    // Check if there are any admins with empty phone numbers or blue numbers
    const hasIncompleteAdmin = admins.some(
      (admin) => !admin.phoneNumber.trim() || !admin.blueNumberId.trim(),
    );

    if (hasIncompleteAdmin) {
      showToast.error('Please complete all existing admin details before adding a new admin.');
      return;
    }

    setAdmins([...admins, { roleId: '', phoneNumber: '', message: '', blueNumberId: '' }]);
  };

  const removeAdmin = (index: number) => {
    setAdmins((prev) => prev.filter((_, i) => i !== index));
  };
  const handleClosePopup = () => setShowPopup(false);

  const handleCreateAnother = () => {
    setShowPopup(false);
    setStep(0);
    setProjectName('');
    setSites(defaultSites);
    setAdmins([{ roleId: '', phoneNumber: '', message: '', blueNumberId: '' }]);
    setPrimaryColour('');
    setSecondaryColour('');
    setSelectedEmoji('');
    setDescription('');
    setIsConfirmed(false);
    setError('');
  };

  const handleViewList = () => {
    setShowPopup(false);
    navigate('/dashboard/project-management', {
      state: {
        message: isEditMode ? 'Project updated successfully!' : 'Project created successfully!',
      },
    });
  };

  const isCurrentStepValid = (): boolean => {
    switch (step) {
      case 0:
        return isStep0Valid;
      case 1:
        const hasErrors = roleAssignmentRef.current?.hasErrors() || false;
        return !hasErrors && isStep1Valid;
      case 2:
        return isStep2Valid;
      case 3:
        return isConfirmed;
      default:
        return false;
    }
  };

  useEffect(() => {
    dispatch(fetchMetaData());
    dispatch(getAllRoles());
  }, [dispatch]);

  return (
    <PageHeaderWrapper
      title={isEditMode ? 'Edit Project' : 'Create New Project'}
      description={isEditMode ? 'Update the project details.' : 'Start off with a new project.'}
      onAdd={() => navigate(-1)}
      addLabel="Return to Project Management"
      showExport={false}
    >
      {/* Stepper */}
      <Stepper
        steps={[
          { title: 'Basic Information', subtitle: 'Enter basic details about project' },
          {
            title: 'Assign System Admin Users',
            subtitle: 'Add admins to manage project',
          },
          {
            title: 'Project Site Locations',
            subtitle: 'Define the geolocation of sites',
          },
          {
            title: `Review & ${isEditMode ? 'Update' : 'Create'}`,
            subtitle: 'Final check & confirmation',
          },
        ]}
        activeStep={step}
      />
      {/* Content */}
      <div className="bg-white rounded-xl">
        {step === 0 && (
          <BasicInformation
            projectName={projectName}
            setProjectName={setProjectName}
            primaryColor={primaryColour}
            setPrimaryColor={setPrimaryColour}
            secondaryColor={secondaryColour}
            setSecondaryColor={setSecondaryColour}
            selectedEmoji={selectedEmoji}
            setSelectedEmoji={setSelectedEmoji}
            description={description}
            setDescription={setDescription}
            onValidationChange={(valid) => setIsStep0Valid(valid)}
            isEditMode={isEditMode}
          />
        )}
        {step === 1 && (
          <RoleAssignment
            // ref={roleAssignmentRef}
            key={resetKey}
            admins={admins}
            onAdminChange={handleAdminChange}
            addAdmin={addAdmin}
            onValidationChange={setIsStep1Valid}
            removeAdmin={removeAdmin}
            isEditMode={isEditMode}
          />
        )}
        {step === 2 && (
          <ProjectSiteLocations
            sites={sites}
            handleSiteChange={handleSiteChange}
            addSite={addSite}
          />
        )}
        {step === 3 && (
          <ReviewAndCreate
            projectName={projectName}
            sites={sites}
            primaryColor={primaryColour}
            secondaryColor={secondaryColour}
            selectedEmoji={selectedEmoji}
            description={description}
            admins={admins}
            isConfirmed={isConfirmed}
            setIsConfirmed={setIsConfirmed}
            isEditMode={isEditMode}
          />
        )}
        <div className="px-6 pb-6 pt-2">
          <PrevNext
            hidePrevious={step === 0}
            prev={handleBackStep}
            next={handleNext}
            onReset={handleReset}
            create={step === 3}
            createLabel={isEditMode ? 'Update Project →' : 'Create New Project →'}
            onCreate={handleCreate}
            disableCreate={!isConfirmed || isCreating}
            disableNext={!isCurrentStepValid()}
            disableReset={step === 3}
            isEditMode={isEditMode}
          />
        </div>
        {/* {error && (
          <div className="px-6 pb-4">
            <p className="text-red-500 text-sm font-medium">{error}</p>
          </div>
        )} */}
      </div>

      {showPopup && (
        <ProjectCreatedPopup
          pid={isEditMode ? state?.project?.id : project?.id?.toString()}
          locationSite={sites[0]?.name || 'N/A'}
          projectTitle={projectName}
          title={isEditMode ? 'Project Updated Successfully' : 'New Project Created Successfully'}
          actions={
            isEditMode
              ? ['Project access granted to assigned admins', 'Audit log entry recorded']
              : ['Role-based access granted', 'Audit log entry recorded']
          }
          onClose={handleClosePopup}
          onCreateAnother={!isEditMode ? handleCreateAnother : undefined}
          onViewList={handleViewList}
          hideCreateAnother={isEditMode}
        />
      )}
    </PageHeaderWrapper>
  );
};

export default CreateProject;
